import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { OperatingCanvasFigure } from '@/components/OperatingCanvasFigure';

export default function OperatingCanvas() {
  return (
    <div className="bg-paper">
      <article id="article-start" data-guide-label="Start" className="mx-auto max-w-3xl px-6 py-20 md:py-24">
        <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
          Operating Canvas
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight tracking-tight md:text-5xl">
          The Operating Canvas: eight lenses on one business
        </h1>
        <p className="mt-3 text-sm text-ink/55">~6 minute read</p>

        <div className="mt-10">
          <OperatingCanvasFigure />
        </div>

        <div className="mt-12 space-y-6 text-lg leading-relaxed text-ink/80">
          <p>
            The Business Model Canvas was a breakthrough. It took the hidden
            assumptions every founder carries — about customers, channels,
            costs, revenue — and laid them on one page where the gaps could be
            seen. Hundreds of thousands of teams have used it to start.
          </p>
          <p>
            What it doesn't show you is <em>how the business actually runs</em>.
          </p>
          <p>
            A canvas tells you what the business <em>is</em>. It doesn't tell
            you how money moves, where data drifts, which decisions are stuck,
            what proof exists for what's being claimed, or how delivery happens
            after the model closes. For most operators I work with, the gap
            between "what the business is" and "what running it actually looks
            like" is where the real cost lives.
          </p>
          <p>
            So I work with eight lenses instead of one. The Business Model
            Canvas is one of them. The other seven are sometimes named in MBA
            programs, sometimes not. Together they form what I call the
            Operating Canvas — not a new framework, just a discipline of
            running all eight before deciding.
          </p>
        </div>

        <h2 id="eight-lenses" data-guide-label="Eight lenses" className="mt-14 font-display text-2xl tracking-tight md:text-3xl">
          The eight lenses
        </h2>

        <div className="mt-8 space-y-6 text-ink/80">
          <p>
            <strong className="text-ink">1. Business.</strong> Who. What. Why.
            Where. For whom. This is the classic canvas territory — value
            proposition, customer segments, channels, key activities,
            partners. Necessary, not sufficient.
          </p>
          <p>
            <strong className="text-ink">2. Revenue.</strong> Where money
            actually comes from, how often, how reliably, how concentrated.
            Most founders can name their revenue <em>streams</em> but not
            their revenue <em>cadence</em> — and cadence is what determines
            whether a business can survive the next quarter.
          </p>
          <p>
            <strong className="text-ink">3. Cost.</strong> Fixed, variable,
            hidden, and opportunity. The hidden costs are usually the
            largest: time the founder spends recompiling numbers, decisions
            delayed waiting for clarity, opportunities missed because the
            data wasn't legible.
          </p>
          <p>
            <strong className="text-ink">4. Process.</strong> Inputs → steps
            → decisions → outputs. This is where work actually happens. Most
            businesses can't draw their own processes accurately on the
            first try. Drawing them is half the value of any engagement.
          </p>
          <p>
            <strong className="text-ink">5. Data.</strong> Sources of truth.
            Flows. Drift. Owners. The single most expensive question in a
            small business: "When two systems disagree, which one is
            right?" If you don't know, you have a data model problem dressed
            as a finance problem.
          </p>
          <p>
            <strong className="text-ink">6. Risk.</strong> Surfaces. Heat.
            Decay. Mitigations. Risk isn't a register — it's a map of where
            the business is fragile and where it isn't. Most risk registers
            are written. Most risk <em>maps</em> are not.
          </p>
          <p>
            <strong className="text-ink">7. Proof.</strong> Evidence class.
            Receipt. Acceptance. What does the business actually have
            evidence for, versus what does it claim? The most common
            pattern: claimed cleanliness with unproven invariants. A book
            that balances doesn't mean a book that's right.
          </p>
          <p>
            <strong className="text-ink">8. Delivery.</strong> Artifact.
            Audience. Cadence. Boundary. How things actually land. The best
            diagnostic in the world is useless if the audience can't read
            it or the cadence doesn't match how the team operates.
          </p>
        </div>

        <h2 id="why-eight" data-guide-label="Why eight" className="mt-14 font-display text-2xl tracking-tight md:text-3xl">
          Why eight, not one
        </h2>

        <div className="mt-8 space-y-6 text-ink/80">
          <p>
            Any one of these lenses can produce a coherent-sounding answer.
            Eight together produce a coherent-sounding business.
          </p>
          <ul className="space-y-2 border-l-2 border-accent pl-6 text-base text-ink/75">
            <li>A purely <strong>Business</strong> answer might say: "We need to expand into segment X."</li>
            <li>A purely <strong>Revenue</strong> answer might say: "We need to raise prices."</li>
            <li>A purely <strong>Cost</strong> answer might say: "We need to cut the cloud bill."</li>
            <li>A purely <strong>Process</strong> answer might say: "We need to automate close."</li>
            <li>A purely <strong>Data</strong> answer might say: "We need a new tool."</li>
            <li>A purely <strong>Risk</strong> answer might say: "We need a backup plan."</li>
            <li>A purely <strong>Proof</strong> answer might say: "We need an audit."</li>
            <li>A purely <strong>Delivery</strong> answer might say: "We need better dashboards."</li>
          </ul>
          <p>
            Each can be right. Each can also be the wrong answer, depending on
            which other lenses you've ignored. The discipline is to{' '}
            <em>run all eight</em> before deciding which one to act on.
          </p>
        </div>

        <h2 id="how-i-use-it" data-guide-label="How I use it" className="mt-14 font-display text-2xl tracking-tight md:text-3xl">
          How I use it
        </h2>

        <div className="mt-8 space-y-6 text-ink/80">
          <p>
            Before any engagement, I run a partial Operating Canvas from the
            prospect's intake answers. The intake doesn't ask all eight — it
            asks six questions that touch all eight. That's enough to know
            which lens is the most worth deepening.
          </p>
          <p>
            If the bottleneck is in <strong>Data</strong> and{' '}
            <strong>Process</strong>, the engagement is a Diagrammed Clarity
            Map. If the bottleneck is in <strong>Decision</strong> (a subset of
            Process) and <strong>Risk</strong>, it's a Decision Map. If the
            bottleneck is in <strong>Process</strong> end-to-end, it's a
            Diagrammed Clarity Map. If the answer is "we need to build the
            thing", it's Operator-Build Support.
          </p>
          <p>Same canvas. Different lens depth. Different deliverable.</p>
        </div>

        <h2 id="what-this-isnt" data-guide-label="What this isn't" className="mt-14 font-display text-2xl tracking-tight md:text-3xl">
          What this isn't
        </h2>

        <div className="mt-8 space-y-6 text-ink/80">
          <p>
            This isn't a packaged formula I'm selling. It's a frame I use. You can
            borrow it. The discipline is not the frame — it's actually running
            all eight lenses before deciding.
          </p>
          <p>
            If you'd like to walk through your own situation with the canvas in
            mind, the intake form is six questions. Each touches one or two
            pillars. Five minutes.
          </p>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-ink/10 pt-10 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-ink/60">
            Articles may use AI-assisted research, outlining, drafting, and
            editing. Final claims and published judgment remain JL-reviewed.
          </p>
          <Button asChild size="lg">
            <Link to="/work-with-me">Send the shape →</Link>
          </Button>
        </div>
      </article>
    </div>
  );
}
